import React from 'react';
import { useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DSBox from '../../../design-system/DSBox';
import DSIconButton from '../../../design-system/DSIconButton';
import DSPaper from '../../../design-system/Paper/DSPaper';
import { DSTypography } from '../../../design-system/Typography';
import KlydoAnimation from '../../../components/KlydoPlayer/KlydoAnimation';
import useInputsDragAndDrop from '../hooks/useInputsDragAndDrop';
import { FILE_VALIDATION } from '../../../consts/dragAndDropInputs';

type KlydoFileInputUploadedProps = {
  klydoSecureUrl: string;
  fileName: string;
  onRemoveFile: () => void;
  onFileChange?: (file: File) => void;
};

const KlydoFileInputUploaded = ({
  klydoSecureUrl,
  fileName,
  onRemoveFile,
  onFileChange,
}: KlydoFileInputUploadedProps) => {
  const { spacing, palette } = useTheme();

  const { isDragging, dragHandlers } = useInputsDragAndDrop({
    onFileAccepted: onFileChange || (() => undefined),
    validation: FILE_VALIDATION.video,
    enabled: !!onFileChange,
  });

  return (
    <DSPaper
      {...dragHandlers}
      sx={{
        display: 'flex',
        height: spacing(15),
        padding: spacing(1, 4),
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
        borderRadius: spacing(3),
        border: isDragging
          ? `2px dashed ${palette.primary.main}`
          : `2px solid transparent`,
        backgroundColor: isDragging ? palette.white[10] : palette.white[5],
        transition: 'all 0.2s ease',
      }}
    >
      <DSBox
        sx={{
          flexDirection: 'row',
          display: 'flex',
          alignItems: 'center',
          gap: spacing(2),
          width: '90%',
        }}
      >
        <DSBox
          sx={{
            width: spacing(9),
            height: spacing(9),
            borderRadius: '50%',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <KlydoAnimation loopUrl={klydoSecureUrl} />
        </DSBox>
        <DSTypography
          sx={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            color: palette.text.primary,
          }}
        >
          {fileName}
        </DSTypography>
      </DSBox>
      <DSIconButton
        onClick={onRemoveFile}
        sx={{
          padding: spacing(1),
          '&:hover': {
            backgroundColor: palette.white[10],
          },
          color: palette.text.secondary,
          transition: 'all 0.3s ease',
        }}
      >
        <CloseIcon />
      </DSIconButton>
    </DSPaper>
  );
};

export default KlydoFileInputUploaded;
